import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { toast } from '../utils/toast';
import {
  Pagination,
  LoadingState,
  EmptyState,
  Breadcrumbs,
  BreadcrumbItem,
} from '../components/ui';
import {
  Globe,
  Building2,
  Calendar,
  CheckCircle2,
  XCircle,
  BarChart3,
  Clock,
  X,
  AlertTriangle,
  Loader2,
} from 'lucide-react';

interface CorsRequest {
  id: string;
  application_id: string;
  client_id: string;
  origin: string;
  environment: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  approved_by?: string;
  approved_at?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
  application_name: string;
  current_origins: string[];
  client_name: string;
  client_email: string;
  client_phone?: string;
}

export const CorsApprovals: React.FC = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>(
    'pending'
  );
  const [selectedRequest, setSelectedRequest] = useState<CorsRequest | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [expiresInDays, setExpiresInDays] = useState<string>('');

  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);

  // Close modals on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowApproveModal(false);
        setShowRejectModal(false);
        setSelectedRequest(null);
        setAdminNote('');
        setExpiresInDays('');
        setRejectionReason('');
        setShowAnalyticsModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Reset offset when filter changes
  useEffect(() => {
    setOffset(0);
  }, [statusFilter, limit]);

  // Fetch CORS requests
  const { data, isLoading } = useQuery<{ data: CorsRequest[]; pagination: { total: number } }>({
    queryKey: ['admin-cors-requests', statusFilter, offset, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      const response = await api.get<{
        success: boolean;
        data: CorsRequest[];
        pagination: { total: number };
      }>(`/portal/admin/cors-requests?${params.toString()}`);
      return response.data;
    },
  });

  const requests = data?.data || [];
  const total = data?.pagination?.total || 0;

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: async (mutationData: {
      requestId: string;
      admin_note?: string;
      expires_in_days?: number;
    }) => {
      const response = await api.post(
        `/portal/admin/cors-requests/${mutationData.requestId}/approve`,
        {
          admin_note: mutationData.admin_note,
          expires_in_days: mutationData.expires_in_days,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Pedido CORS aprovado com sucesso!');
      void queryClient.invalidateQueries({ queryKey: ['admin-cors-requests'] });
      setShowApproveModal(false);
      setSelectedRequest(null);
      setAdminNote('');
    },
    onError: () => {
      toast.error('Falha ao aprovar pedido CORS');
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: async (mutationData: { requestId: string; reason: string }) => {
      const response = await api.post(
        `/portal/admin/cors-requests/${mutationData.requestId}/reject`,
        {
          reason: mutationData.reason,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Pedido CORS rejeitado');
      void queryClient.invalidateQueries({ queryKey: ['admin-cors-requests'] });
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectionReason('');
    },
    onError: () => {
      toast.error('Falha ao rejeitar pedido CORS');
    },
  });

  const handleApprove = (request: CorsRequest) => {
    setSelectedRequest(request);
    setShowApproveModal(true);
  };

  const handleReject = (request: CorsRequest) => {
    setSelectedRequest(request);
    setShowRejectModal(true);
  };

  const confirmApprove = () => {
    if (selectedRequest) {
      approveMutation.mutate({
        requestId: selectedRequest.id,
        admin_note: adminNote,
        expires_in_days: expiresInDays ? parseInt(expiresInDays) : undefined,
      });
    }
  };

  const handleViewAnalytics = async (request: CorsRequest) => {
    setSelectedRequest(request);
    setShowAnalyticsModal(true);
    setIsLoadingAnalytics(true);
    try {
      const res = await api.get(`/portal/admin/cors-requests/${request.id}/analytics`);
      setAnalyticsData(res.data.data);
    } catch {
      toast.error('Falha ao carregar analítica CORS');
      setShowAnalyticsModal(false);
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  const confirmReject = () => {
    if (selectedRequest && rejectionReason.trim()) {
      rejectMutation.mutate({
        requestId: selectedRequest.id,
        reason: rejectionReason.trim(),
      });
    }
  };

  // Get total pending count
  const { data: pendingData } = useQuery({
    queryKey: ['admin-cors-requests-pending-count'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; pagination: { total: number } }>(
        `/portal/admin/cors-requests?status=pending&limit=1`
      );
      return response.data.pagination.total;
    },
  });
  const pendingCount = pendingData || 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Aprovações CORS</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center">
          <Globe className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">
            Aprovações CORS
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Reveja e autorize pedidos de origens web submetidos pelas aplicações dos clientes.
          </p>
        </div>
      </div>

      {/* Status Filter */}
      <div className="flex items-center gap-2 p-1.5 bg-slate-100 dark:bg-slate-800/60 rounded-2xl w-fit border border-slate-200 dark:border-slate-750">
        {(['all', 'pending', 'approved', 'rejected'] as const).map((status) => {
          const isActive = statusFilter === status;
          return (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                isActive
                  ? 'bg-white dark:bg-slate-900 text-primary-600 dark:text-primary-400 shadow-sm border border-slate-200/60 dark:border-slate-700'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              {status === 'all'
                ? 'Todos'
                : status === 'pending'
                  ? 'Pendentes'
                  : status === 'approved'
                    ? 'Aprovados'
                    : 'Rejeitados'}
              {status === 'pending' && pendingCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-500 text-white">
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Requests List */}
      {isLoading ? (
        <LoadingState type="card" count={2} />
      ) : requests.length > 0 ? (
        <div className="space-y-4">
          {requests.map((request) => (
            <div
              key={request.id}
              className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4 pb-4 border-b border-slate-100 dark:border-slate-800">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-primary-500" />
                    {request.application_name}
                  </h3>
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                      request.status === 'pending'
                        ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
                        : request.status === 'approved'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
                          : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800'
                    }`}
                  >
                    {request.status === 'pending' && <Clock className="w-3 h-3" />}
                    {request.status === 'approved' && <CheckCircle2 className="w-3 h-3" />}
                    {request.status === 'rejected' && <XCircle className="w-3 h-3" />}
                    {request.status === 'pending'
                      ? 'Pendente'
                      : request.status === 'approved'
                        ? 'Aprovado'
                        : 'Rejeitado'}
                  </span>
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                    {request.environment}
                  </span>
                </div>

                <div className="flex items-center gap-2 self-end md:self-auto">
                  {request.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApprove(request)}
                        className="btn-base px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Aprovar
                      </button>
                      <button
                        onClick={() => handleReject(request)}
                        className="btn-base px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Rejeitar
                      </button>
                    </>
                  )}
                  {request.status === 'approved' && (
                    <button
                      onClick={() => handleViewAnalytics(request)}
                      className="btn-base btn-secondary px-3.5 py-1.5 text-xs font-semibold rounded-xl flex items-center gap-1.5 text-primary-600 dark:text-primary-400"
                    >
                      <BarChart3 className="w-3.5 h-3.5" /> Ver Analítica
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">
                      Origem Solicitada
                    </span>
                    <code className="inline-block bg-slate-100 dark:bg-slate-800 text-rose-600 dark:text-rose-400 px-2.5 py-1 rounded-lg text-xs font-mono font-bold border border-slate-200 dark:border-slate-700">
                      {request.origin}
                    </code>
                  </div>

                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-0.5">
                      Cliente Responsável
                    </span>
                    <span className="font-semibold text-slate-900 dark:text-white">
                      {request.client_name}
                    </span>{' '}
                    <span className="text-slate-500 text-xs">({request.client_email})</span>
                  </div>

                  <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>
                      Solicitado em {new Date(request.created_at).toLocaleDateString('pt-PT')}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">
                      Justificação / Motivo
                    </span>
                    <p className="text-xs text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-100 dark:border-slate-750 leading-relaxed">
                      {request.reason}
                    </p>
                  </div>

                  {request.rejection_reason && (
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-rose-500 block mb-1">
                        Motivo da Rejeição
                      </span>
                      <p className="text-xs text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/30 p-3 rounded-xl border border-rose-200 dark:border-rose-900/50 leading-relaxed">
                        {request.rejection_reason}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          <Pagination
            total={total}
            limit={limit}
            offset={offset}
            onPageChange={setOffset}
            onLimitChange={setLimit}
          />
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 text-center shadow-sm">
          <EmptyState
            title="Nenhum pedido CORS encontrado"
            description="Não existem registos de pedidos CORS com o filtro de estado selecionado."
          />
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedRequest && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowApproveModal(false);
              setSelectedRequest(null);
              setAdminNote('');
              setExpiresInDays('');
            }
          }}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-200 dark:border-slate-800">
            <div className="flex justify-between items-center pb-4 border-b border-slate-100 dark:border-slate-800 mb-5">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" /> Aprovar Pedido CORS
              </h3>
              <button
                type="button"
                onClick={() => setShowApproveModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 mb-5 text-xs text-slate-700 dark:text-slate-300">
              <div>
                <span className="text-slate-400 block mb-0.5">Aplicação:</span>
                <p className="font-bold text-slate-900 dark:text-white text-sm">
                  {selectedRequest.application_name}
                </p>
              </div>
              <div>
                <span className="text-slate-400 block mb-1">Origem a autorizar:</span>
                <code className="block bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 border border-slate-200 dark:border-slate-700">
                  {selectedRequest.origin}
                </code>
              </div>
            </div>

            <div className="space-y-4 mb-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                  Nota de Administração (opcional)
                </label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-xs focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                  placeholder="Adicione notas sobre esta aprovação..."
                  rows={2}
                  maxLength={500}
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                  Validade em Dias (opcional)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-xs focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                  placeholder="Ex: 30 (vazio = permanente)"
                />
              </div>
            </div>

            {approveMutation.isError && (
              <div className="mb-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 p-3">
                <p className="text-xs font-medium text-rose-800 dark:text-rose-300">
                  {getErrorMessage(approveMutation.error)}
                </p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowApproveModal(false);
                  setSelectedRequest(null);
                  setAdminNote('');
                }}
                className="btn-base btn-secondary flex-1 text-xs font-semibold py-2.5"
              >
                Cancelar
              </button>
              <button
                onClick={confirmApprove}
                disabled={approveMutation.isPending}
                className="btn-base flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold py-2.5 flex items-center justify-center gap-1.5 shadow-md disabled:opacity-50"
              >
                {approveMutation.isPending ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> A aprovar...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Confirmar Aprovação
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowRejectModal(false);
              setSelectedRequest(null);
              setRejectionReason('');
            }
          }}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-200 dark:border-slate-800">
            <div className="flex justify-between items-center pb-4 border-b border-slate-100 dark:border-slate-800 mb-5">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <XCircle className="w-5 h-5 text-rose-500" /> Rejeitar Pedido CORS
              </h3>
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 mb-5 text-xs text-slate-700 dark:text-slate-300">
              <div>
                <span className="text-slate-400 block mb-0.5">Aplicação:</span>
                <p className="font-bold text-slate-900 dark:text-white text-sm">
                  {selectedRequest.application_name}
                </p>
              </div>
              <div>
                <span className="text-slate-400 block mb-1">Origem rejeitada:</span>
                <code className="block bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg text-xs font-mono font-bold text-rose-600 dark:text-rose-400 border border-slate-200 dark:border-slate-700">
                  {selectedRequest.origin}
                </code>
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                Motivo da Rejeição *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-xs focus:ring-2 focus:ring-rose-500 outline-none transition-all"
                placeholder="Explique o motivo da rejeição (mínimo 10 caracteres)..."
                rows={3}
                minLength={10}
                maxLength={500}
                required
              />
              <p className="text-[11px] text-slate-400 mt-1 text-right">
                {rejectionReason.length}/500 caracteres
              </p>
            </div>

            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-xl p-3 mb-5 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                O cliente será notificado da rejeição com a justificação introduzida.
              </p>
            </div>

            {rejectMutation.isError && (
              <div className="mb-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 p-3">
                <p className="text-xs font-medium text-rose-800 dark:text-rose-300">
                  {getErrorMessage(rejectMutation.error)}
                </p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedRequest(null);
                  setRejectionReason('');
                }}
                className="btn-base btn-secondary flex-1 text-xs font-semibold py-2.5"
              >
                Cancelar
              </button>
              <button
                onClick={confirmReject}
                disabled={rejectMutation.isPending || rejectionReason.length < 10}
                className="btn-base flex-1 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold py-2.5 flex items-center justify-center gap-1.5 shadow-md disabled:opacity-50"
              >
                {rejectMutation.isPending ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> A rejeitar...
                  </>
                ) : (
                  <>
                    <XCircle className="w-3.5 h-3.5" /> Confirmar Rejeição
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalyticsModal && selectedRequest && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAnalyticsModal(false);
              setSelectedRequest(null);
              setAnalyticsData(null);
            }
          }}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-slate-200 dark:border-slate-800">
            <div className="flex justify-between items-center pb-4 border-b border-slate-100 dark:border-slate-800 mb-5">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary-500" /> Analítica de Segurança CORS
              </h3>
              <button
                onClick={() => setShowAnalyticsModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">
                Origem Monitorizada:
              </span>
              <code className="block bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-xl text-xs font-mono font-bold text-rose-600 dark:text-rose-400 border border-slate-200 dark:border-slate-700">
                {selectedRequest.origin}
              </code>
            </div>

            {isLoadingAnalytics ? (
              <div className="flex justify-center p-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : analyticsData ? (
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl border border-slate-100 dark:border-slate-750">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Últimas 24 Horas
                  </p>
                  <p className="text-2xl font-extrabold text-slate-900 dark:text-white mt-1">
                    {analyticsData.metrics.requests_last_24h}
                  </p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                    Pedidos válidos
                  </p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl border border-slate-100 dark:border-slate-750">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Últimos 7 Dias
                  </p>
                  <p className="text-2xl font-extrabold text-slate-900 dark:text-white mt-1">
                    {analyticsData.metrics.requests_last_7d}
                  </p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                    Pedidos totais
                  </p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl border border-slate-100 dark:border-slate-750 col-span-2 flex justify-between items-center">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Taxa de Erro CORS
                    </p>
                    <p className="text-lg font-bold text-rose-600 dark:text-rose-400 mt-0.5">
                      {analyticsData.metrics.error_rate}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Último Acesso
                    </p>
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-0.5">
                      {new Date(analyticsData.metrics.last_accessed).toLocaleDateString('pt-PT')}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-slate-400 text-center py-6 text-xs">Sem dados disponíveis.</p>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowAnalyticsModal(false)}
                className="btn-base btn-secondary text-xs font-semibold px-4 py-2"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
